import type { GameBridge } from './bridge';

/**
 * The one place that decides whether the hunt is paused.
 *
 * ## Why this exists
 *
 * `ui:set-paused` carries a boolean and no notion of WHO asked, so the last
 * publisher wins. That was fine while a hidden tab was the only reason to
 * pause. It stopped being fine the moment a duel became a second reason: a
 * player who opened a duel (paused) and then switched tabs and came back had
 * `visibilitychange` publish `paused: false` on their behalf, and the hunt
 * resumed simulating behind the fight. Switching tabs mid-duel is not exotic -
 * it is exactly what the room's twelve-second reconnect grace exists to
 * tolerate.
 *
 * So the bridge keeps carrying only the aggregate, and the OR happens here.
 *
 * ## Leases, not a boolean per reason
 *
 * `setReason('pvp', false)` looks equivalent and is not. React's StrictMode
 * mounts an effect, cleans it up and mounts it again, so the cleanup of the
 * FIRST mount would clear a reason the second mount is still holding - and the
 * hunt would resume underneath a live duel because a development-mode
 * remount happened to be tidy.
 *
 * A lease cannot do that. Acquiring returns a release that only ever removes
 * ITS OWN token: two leases on one reason keep the pause until both are gone,
 * releasing twice is a no-op, and releasing out of order is not a thing that
 * can be got wrong.
 *
 * ## It publishes only when the answer changes
 *
 * The clock ignores a value it already holds, so a repeat costs nothing - but
 * a component that re-renders is not an event, and a bridge message per render
 * is a message somebody eventually counts.
 */

/** Why the hunt might be paused. Both are visual; neither moves an economy. */
export type PauseReason = 'visibility' | 'pvp' | 'open_world';

/** Removes exactly the lease it came from. Safe to call any number of times. */
export type PauseLease = () => void;

export class PauseCoordinator {
  private readonly held = new Map<PauseReason, Set<symbol>>();
  private published = false;

  constructor(private readonly bridge: GameBridge) {}

  /** Whether anything is currently holding the hunt paused. */
  get paused(): boolean {
    for (const tokens of this.held.values()) {
      if (tokens.size > 0) {
        return true;
      }
    }
    return false;
  }

  /** Which reasons are holding it. For tests and for a readable failure. */
  reasons(): PauseReason[] {
    return [...this.held.entries()]
      .filter(([, tokens]) => tokens.size > 0)
      .map(([reason]) => reason)
      .sort();
  }

  /**
   * Takes a lease on a reason, and hands back the only thing that can drop it.
   *
   * The token is a `symbol` so two leases on the same reason are genuinely two
   * things: a counter would be equivalent until somebody released one twice,
   * and then it would be one release short of a live duel resuming the hunt.
   */
  acquire(reason: PauseReason): PauseLease {
    const token = Symbol(reason);
    let tokens = this.held.get(reason);
    if (!tokens) {
      tokens = new Set();
      this.held.set(reason, tokens);
    }
    tokens.add(token);
    this.settle();

    let released = false;
    return () => {
      if (released) {
        // Idempotent on purpose. A cleanup that runs twice - StrictMode, an
        // error path, a defensive caller - must not drop somebody else's hold.
        return;
      }
      released = true;
      this.held.get(reason)?.delete(token);
      this.settle();
    };
  }

  /**
   * Sends the current aggregate again, whether or not it changed.
   *
   * For `game:ready` and nothing else: React attaches its listeners while the
   * asset manifest is still loading, so the first publication happens before
   * the scene has subscribed, and the bridge has no replay - deliberately,
   * because a queue would also redeliver stale commands. Republishing once the
   * scene is listening closes that window without buffering anything.
   */
  republish(): void {
    this.published = this.paused;
    this.bridge.sendToGame('ui:set-paused', { paused: this.published });
  }

  private settle(): void {
    const paused = this.paused;
    if (paused === this.published) {
      return;
    }
    this.published = paused;
    this.bridge.sendToGame('ui:set-paused', { paused });
  }
}
